import {
  number, string, date, mixed,
  array,
} from 'yup';
import { compact, isEmpty } from 'lodash';
import { getExternalUrl, getStreamingProfileDetails } from '@sda/utils/url';
import { getDateTime } from '@sda/utils/date';
import type { DateRange } from '@mui/x-date-pickers-pro';
import type { ref } from 'yup';
import type { DateTime } from 'luxon';

type Reference<T> = ReturnType<typeof ref<T>>;

declare module 'yup' {
  interface ArraySchema<TIn, TContext, TDefault, TFlags> { // eslint-disable-line
    /**
     * Ensures the array contains at least one non-null value
     * @param message The error message
     */
    containsValue(message?: string): this;
  }

  interface MixedSchema {
    /**
     * Ensures that the value exists if the given reference exists
     * @param reference Reference to check against
     * @param message Error message
     */
    depends<T = unknown>(reference: Reference<T>, message: string): this;

    /**
     * Ensures that the value exists if the given reference exists or if another field is empty
     * @param reference First reference to check against (exists)
     * @param otherRef Second reference to check against (empty)
     * @param message Error message
     */
    dependsOrEmpty<T = unknown, R = unknown>(reference: Reference<T>, otherRef: Reference<R>, message: string): this;

    /**
     * Ensures the value does not match the value of the given reference
     * @param reference The reference to check against
     * @param message Error message
     */
    nomatch<T = unknown>(reference: Reference<T>, message: string): this;
  }

  interface DateSchema {
    /**
     * Ensures that the value exists if the given reference exists
     * @param reference Reference to check against
     * @param message Error message
     */
    depends<T = unknown>(reference: Reference<T>, message: string): this;

    /**
     * Ensures that the field is within 90 days after the reference value
     * @param reference Reference to check against
     * @param days Number of days from reference
     * @param message Error message
     */
    within<T = DateTime>(reference: Reference<T>, days?: number, message?: string): this;
  }

  interface StringSchema {
    /**
     * Ensures that the value exists if the given reference exists
     * @param reference Reference to check against
     * @param message Error message
     */
    depends<T = unknown>(reference: Reference<T>, message: string): this;

    /**
     * Ensures that the value exists if the given reference exists or if another field is empty
     * @param reference First reference to check against (exists)
     * @param otherRef Second reference to check against (empty)
     * @param message Error message
     */
    dependsOrEmpty<T = unknown, R = unknown>(reference: Reference<T>, otherRef: Reference<R>, message: string): this;

    /**
     * Ensures the value is a valid URL
     */
    http(): this;

    /**
     * Ensures the value is less than the price
     */
    minimumPrice(): this;

    /**
     * Ensures the value does not match the value of the given reference
     * @param reference The reference to check against
     * @param message Error message
     */
    nomatch<T = unknown>(reference: Reference<T>, message: string): this;

    /**
     * Ensures the value is a valid streaming URI
     * @param provider The name of the provider
     */
    streamingUri(provider: string): this;
  }

  interface NumberSchema {
    /**
     * Divides the value by the divisor
     * @param divisor
     */
    divide(divisor: number): this;
  }
}

/* eslint-disable func-names */
/* eslint-disable prefer-arrow-callback */
date.prototype.within = function (ref: Reference<any>, days?: number, message?: string) {
  const testDays = days || 90;

  return this.test(
    'within',
    message || `Value must be within ${testDays} of ${ref.key}`,
    function (value) {
      const field = this.resolve(ref);

      return getDateTime(value) <= getDateTime(field).plus({ day: testDays });
    },
  );
};

number.prototype.divide = function (divisor: number) {
  return this.transform(function (dividend: number) {
    return dividend / divisor;
  });
};

string.prototype.http = function () {
  return this.transform(function (url: string) {
    if (!url || url.length === 0) {
      return '';
    }

    return getExternalUrl(url);
  });
};

const depends = function (ref: Reference<any>, message: string) {
  return this.test(
    'depends',
    message || 'This field is required.',
    function () {
      const other = this.resolve(ref);

      return !!other && other.toString().length;
    },
  );
};

mixed.prototype.depends = depends;
string.prototype.depends = depends;

const dependsOrEmpty = function (ref: Reference<any>, otherRef: Reference<any>, message: string) {
  return this.test(
    'dependsOrEmpty',
    message || 'This field is required.',
    function () {
      const field = this.resolve(ref);
      const other = this.resolve(otherRef);

      return !field || (!!other && other.toString().length);
    },
  );
};

mixed.prototype.dependsOrEmpty = dependsOrEmpty;
string.prototype.dependsOrEmpty = dependsOrEmpty;

const nomatch = function (ref: Reference<any>, message: string) {
  return this.test(
    'nomatch',
    message || 'Please ensure this field is not already in use.',
    function (value: string) {
      const other = this.resolve(ref);

      return value !== other.toString();
    },
  );
};

mixed.prototype.nomatch = nomatch;
string.prototype.nomatch = nomatch;

string.prototype.streamingUri = function (provider: string) {
  return this.test(
    'streamingUri',
    `Please enter a valid ${provider} url`,
    function (uri: string) {
      if (!uri) {
        return true;
      }

      const { service, artistId } = getStreamingProfileDetails(uri);
      return !!service && !!artistId;
    },
  );
};

string.prototype.minimumPrice = function () {
  return this.test('minimumPrice', function () {
    if (this.parent && this.parent.hasVariablePrice === false) {
      return true;
    }

    if (this.parent && this.parent.quantity > 0 && parseFloat(this.parent.price || this.parent.ticketPrice || 0) < this.parent.minimumPrice) {
      return false;
    }

    return true;
  });
};

array.prototype.containsValue = function (message: string) {
  return this.test(
    'containsValue',
    message || 'Please ensure this field is not already in use.',
    function (value: DateRange<DateTime>) {
      return !isEmpty(compact(value));
    },
  );
};
